import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Text } from 'react-native';
import { trpc } from '../lib/trpc';
import {
  FloatingActionBanner,
  FloatingActionBannerButton,
} from './FloatingActionBanner';

type RecalcState = 'idle' | 'scheduled' | 'paused' | 'running' | 'error';

export function FireRecalcBanner() {
  const utils = trpc.useUtils();
  const prevStateRef = useRef<RecalcState | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const statusQ = trpc.fire.getRecalcStatus.useQuery(undefined, {
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const pauseMutation = trpc.fire.pauseRecalc.useMutation({
    onSuccess: () => {
      void statusQ.refetch();
    },
  });

  const runNowMutation = trpc.fire.runRecalcNow.useMutation({
    onSuccess: () => {
      void statusQ.refetch();
    },
  });

  const { refetch } = statusQ;
  const state = statusQ.data?.state ?? 'idle';

  useEffect(() => {
    if (state !== 'scheduled' && state !== 'running') return;

    const timer = setInterval(() => {
      void refetch();
    }, 1000);

    return () => clearInterval(timer);
  }, [refetch, state]);

  useEffect(() => {
    if (state !== 'scheduled') return;

    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [state]);

  useEffect(() => {
    const previous = prevStateRef.current;
    if (previous === 'running' && state !== 'running') {
      void Promise.allSettled([
        utils.fire.getPlan.invalidate(),
        utils.fire.getLastRecalcImpact.invalidate(),
      ]);
    }
    prevStateRef.current = state;
  }, [state, utils.fire.getLastRecalcImpact, utils.fire.getPlan]);

  const countdown = useMemo(() => {
    const scheduledFor = statusQ.data?.scheduledFor;
    if (!scheduledFor) return null;
    const ms = new Date(scheduledFor).getTime() - now;
    return Math.max(0, Math.ceil(ms / 1000));
  }, [statusQ.data?.scheduledFor, now]);

  if (!statusQ.data) return null;
  if (state === 'idle') return null;

  const runNowAction = (
    <FloatingActionBannerButton
      variant="primary"
      onPress={() => runNowMutation.mutate()}
      loading={runNowMutation.isPending}
      disabled={pauseMutation.isPending}
    >
      Run now
    </FloatingActionBannerButton>
  );

  if (state === 'running') {
    return (
      <FloatingActionBanner
        title="Recalculating FIRE plan"
        message="Refreshing projections with your latest changes."
        iconElement={<ActivityIndicator size="small" color="#ffffff" />}
        tone="success"
        desktopMaxWidth={420}
        desktopMinWidth={360}
        desktopTopOffset={16}
      />
    );
  }

  if (state === 'scheduled') {
    return (
      <FloatingActionBanner
        title="FIRE recalc scheduled"
        message={
          <>
            Starting in{' '}
            <Text className="font-semibold text-forest-600 dark:text-green-500">
              {countdown ?? 0}s
            </Text>
            .
          </>
        }
        iconName="flame"
        tone="success"
        desktopMaxWidth={420}
        desktopMinWidth={360}
        desktopTopOffset={16}
        actions={
          <>
            <FloatingActionBannerButton
              onPress={() => pauseMutation.mutate()}
              loading={pauseMutation.isPending}
              disabled={runNowMutation.isPending}
            >
              Wait
            </FloatingActionBannerButton>
            {runNowAction}
          </>
        }
      />
    );
  }

  if (state === 'paused') {
    return (
      <FloatingActionBanner
        title="FIRE recalc paused"
        message="Run it when you are ready to refresh your plan."
        iconName="pause"
        tone="warning"
        desktopMaxWidth={420}
        desktopMinWidth={360}
        desktopTopOffset={16}
        actions={runNowAction}
      />
    );
  }

  return (
    <FloatingActionBanner
      title="FIRE recalc failed"
      message={
        statusQ.data.lastError ??
        'Something went wrong while refreshing your plan.'
      }
      iconName="alert-circle"
      tone="error"
      desktopMaxWidth={460}
      desktopMinWidth={360}
      desktopTopOffset={16}
      actions={runNowAction}
    />
  );
}
